import {
  cloneJSONValue,
  messageVersionValue,
  nullableStringValue,
  numberValue,
  recordValue,
  stringValue
} from "./activityValueParsing.ts";
import type {
  AgentActivityMessage,
  AgentActivityUpdatedEvent
} from "./types.ts";

/**
 * Parse the inline `messages` array carried by a realtime `message_update`
 * event into canonical {@link AgentActivityMessage} values. This is the only
 * inline delta the bridge may apply without a follow-up pull: message snapshots
 * are versioned and self-describing, so after the bridge verifies cursor
 * continuity, the engine's version-guarded message reducer can fold them
 * (including alias-bucket collapse) on its own. Turn and interaction deltas take
 * the authoritative reconcile path instead, so their session-level effects
 * (activeTurnId, pending-interaction pruning) come from a full session fetch
 * rather than being reconstructed here.
 */
export function parseInlineActivityMessages(
  event: AgentActivityUpdatedEvent
): AgentActivityMessage[] {
  if (event.eventType === "session_audit") {
    const audit = recordValue(event.data.audit);
    if (!audit) return [];
    return [
      agentActivityMessageFromInlineMessage({
        agentSessionId: event.agentSessionId.trim(),
        message: {
          kind: "session_audit",
          messageId: audit.auditId,
          occurredAtUnixMs: audit.occurredAtUnixMs,
          payload: audit.payload,
          role: audit.role,
          turnId: null,
          version: audit.version
        },
        workspaceId: event.workspaceId
      })
    ].filter((message): message is AgentActivityMessage => message !== null);
  }
  if (event.eventType !== "message_update") return [];
  const source = recordValue(event.data);
  const rawMessages = Array.isArray(source?.messages) ? source.messages : [];
  const eventAgentSessionId = event.agentSessionId.trim();
  const workspaceId = event.workspaceId?.trim() ?? "";
  const messages: AgentActivityMessage[] = [];
  for (const raw of rawMessages) {
    const record = recordValue(raw);
    if (!record) continue;
    const agentSessionId =
      stringValue(record.agentSessionId) || eventAgentSessionId;
    const message = agentActivityMessageFromInlineMessage({
      agentSessionId,
      message: record,
      workspaceId
    });
    if (message) messages.push(message);
  }
  return messages;
}

function agentActivityMessageFromInlineMessage(input: {
  agentSessionId: string;
  message: Record<string, unknown>;
  workspaceId: string;
}): AgentActivityMessage | null {
  const messageId = stringValue(input.message.messageId);
  const role = stringValue(input.message.role);
  const kind = stringValue(input.message.kind);
  const rawTurnId = input.message.turnId;
  const turnId = rawTurnId === null ? null : stringValue(rawTurnId);
  const version = messageVersionValue(input.message);
  const occurredAtUnixMs = numberValue(input.message.occurredAtUnixMs);
  if (
    !input.agentSessionId ||
    !messageId ||
    !role ||
    !kind ||
    (rawTurnId !== null && !turnId) ||
    (kind === "session_audit" ? rawTurnId !== null : rawTurnId === null) ||
    version <= 0 ||
    occurredAtUnixMs === undefined ||
    occurredAtUnixMs <= 0
  ) {
    return null;
  }
  return {
    workspaceId: stringValue(input.message.workspaceId) || input.workspaceId,
    agentSessionId: input.agentSessionId,
    messageId,
    version,
    turnId,
    role,
    kind,
    status: nullableStringValue(input.message.status),
    semantics: recordValue(input.message.semantics)
      ? (cloneJSONValue(
          input.message.semantics
        ) as AgentActivityMessage["semantics"])
      : undefined,
    payload: cloneJSONValue(recordValue(input.message.payload) ?? {}) as Record<
      string,
      unknown
    >,
    sequence: numberValue(input.message.sequence),
    occurredAtUnixMs,
    createdAtUnixMs: numberValue(input.message.createdAtUnixMs),
    startedAtUnixMs: numberValue(input.message.startedAtUnixMs),
    completedAtUnixMs: numberValue(input.message.completedAtUnixMs)
  };
}
